
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../dashboard/components/Sidebar';
import DashboardHeader from '../../dashboard/components/DashboardHeader';
import Description from '../components/Description';
import Photos from '../components/Photos';
import PDFs from '../components/PDFs';
import Agreements from '../components/Agreements';
import Invoices from '../components/Invoices';
import Notes from '../components/Notes';

const ProjectDetails = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedProject, setSelectedProject] = useState<string | null>(projectId);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);

  // Auto-select project from URL parameter
  useEffect(() => {
    if (projectId) {
      setSelectedProject(projectId);
    }
  }, [projectId]);

  const categoryNames: { [key: string]: string } = {
    'private-clients': 'Private Clients',
    'securitas': 'Securitas',
    'cic': 'CIC',
    'johnson-controls': 'Johnson Controls',
    'others': 'Others'
  };

  const categoryProjects: { [key: string]: any[] } = {
    'private-clients': [
      {
        id: '1',
        name: 'Sunset Villa Renovation',
        client: 'Johnson Family',
        status: 'In Progress',
        progress: 65,
        budget: '$125,000',
        deadline: '2024-03-15',
        type: 'Residential',
        priority: 'High',
        address: '123 Sunset Boulevard, Beverly Hills, CA 90210'
      },
      {
        id: '2',
        name: 'Maple Street Apartments',
        client: 'City Housing Authority',
        status: 'In Progress',
        progress: 80,
        budget: '$450,000',
        deadline: '2024-02-28',
        type: 'Residential',
        priority: 'High',
        address: '789 Maple Street, Residential District, CA 90210'
      },
      {
        id: '3',
        name: 'Modern Family Home',
        client: 'Smith Family',
        status: 'Planning',
        progress: 15,
        budget: '$280,000',
        deadline: '2024-05-20',
        type: 'Residential',
        priority: 'Medium',
        address: '456 Oak Avenue, Suburban Hills, CA 90211'
      }
    ],
    'securitas': [
      {
        id: '4',
        name: 'Security Command Center',
        client: 'Securitas AB',
        status: 'In Progress',
        progress: 45,
        budget: '$320,000',
        deadline: '2024-04-10',
        type: 'Security Infrastructure',
        priority: 'High',
        address: '100 Security Plaza, Downtown, CA 90013'
      },
      {
        id: '5',
        name: 'Perimeter Fence Installation',
        client: 'Securitas Regional',
        status: 'Completed',
        progress: 100,
        budget: '$85,000',
        deadline: '2024-01-30',
        type: 'Security Infrastructure',
        priority: 'Medium',
        address: '200 Industrial Park, Commerce, CA 90040'
      }
    ],
    'cic': [
      {
        id: '6',
        name: 'Downtown Office Complex',
        client: 'TechCorp Inc.',
        status: 'Planning',
        progress: 25,
        budget: '$850,000',
        deadline: '2024-06-30',
        type: 'Commercial',
        priority: 'Medium',
        address: '456 Business Ave, Downtown, CA 90013'
      },
      {
        id: '7',
        name: 'Industrial Warehouse',
        client: 'LogiCorp',
        status: 'Completed',
        progress: 100,
        budget: '$320,000',
        deadline: '2024-01-15',
        type: 'Industrial',
        priority: 'Low',
        address: '321 Industrial Way, Commerce, CA 90040'
      },
      {
        id: '8',
        name: 'Retail Store Renovation',
        client: 'Fashion Forward',
        status: 'On Hold',
        progress: 40,
        budget: '$75,000',
        deadline: '2024-04-20',
        type: 'Commercial',
        priority: 'Medium',
        address: '654 Shopping Center, Mall District, CA 90210'
      }
    ],
    'johnson-controls': [
      {
        id: '9',
        name: 'HVAC System Upgrade',
        client: 'Johnson Controls',
        status: 'In Progress',
        progress: 60,
        budget: '$180,000',
        deadline: '2024-03-25',
        type: 'HVAC',
        priority: 'High',
        address: '789 Corporate Center, Business District, CA 90014'
      },
      {
        id: '10',
        name: 'Building Automation Install',
        client: 'Johnson Controls',
        status: 'Planning',
        progress: 20,
        budget: '$220,000',
        deadline: '2024-05-15',
        type: 'Automation',
        priority: 'Medium',
        address: '321 Tech Plaza, Innovation District, CA 90015'
      }
    ],
    'others': [
      {
        id: '11',
        name: 'Historic Building Restoration',
        client: 'Heritage Foundation',
        status: 'In Progress',
        progress: 35,
        budget: '$150,000',
        deadline: '2024-07-10',
        type: 'Restoration',
        priority: 'Low',
        address: '123 Historic Avenue, Old Town, CA 90212'
      },
      {
        id: '12',
        name: 'Emergency Repair Project',
        client: 'City Emergency Services',
        status: 'Completed',
        progress: 100,
        budget: '$45,000',
        deadline: '2024-01-20',
        type: 'Emergency',
        priority: 'High',
        address: '555 Emergency Lane, City Center, CA 90016'
      }
    ]
  };

  const projects = categoryProjects[category || ''] || [];

  const tabs = [
    { id: 'description', label: 'Description', icon: 'ri-file-text-line' },
    { id: 'photos', label: 'Photos', icon: 'ri-image-line' },
    { id: 'pdfs', label: 'PDFs', icon: 'ri-file-pdf-line' },
    { id: 'agreements', label: 'Agreements and Closeout', icon: 'ri-contract-line' },
    { id: 'invoices', label: 'Invoices', icon: 'ri-bill-line' },
    { id: 'notes', label: 'Notes', icon: 'ri-sticky-note-line' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'Planning': return 'bg-yellow-100 text-yellow-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'On Hold': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'text-red-600';
      case 'Medium': return 'text-yellow-600';
      case 'Low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const renderTabContent = () => {
    if (!selectedProject) {
      return (
        <div className="text-center py-12">
          <i className="ri-folder-open-line text-6xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Project</h3>
          <p className="text-gray-500">Choose a project from the list above to view its details and manage files.</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'description':
        return <Description projectId={selectedProject} />;
      case 'photos':
        return <Photos projectId={selectedProject} />;
      case 'pdfs':
        return <PDFs projectId={selectedProject} />;
      case 'agreements':
        return <Agreements projectId={selectedProject} />;
      case 'invoices':
        return <Invoices projectId={selectedProject} />;
      case 'notes':
        return <Notes projectId={selectedProject} />;
      default:
        return <Description projectId={selectedProject} />;
    }
  };

  const selectedProjectData = projects.find(p => p.id === selectedProject);

  // If no projects in category, show not found message
  if (projects.length === 0) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <DashboardHeader onMenuClick={() => setSidebarOpen(true)} />
          
          <div className="flex-1 overflow-auto">
            <div className="p-6">
              <div className="flex items-center mb-6">
                <button
                  onClick={() => navigate('/projects')}
                  className="flex items-center text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  <span className="text-sm font-medium">Back to Projects</span>
                </button>
                <i className="ri-arrow-right-s-line text-gray-400 mx-2"></i>
                <span className="text-sm text-gray-600">{categoryNames[category || '']}</span>
              </div>

              <div className="text-center py-12">
                <i className="ri-folder-open-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Projects Found</h3>
                <p className="text-gray-500 mb-6">No projects available in this category yet.</p>
                <button
                  onClick={() => navigate('/projects')}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Projects
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <DashboardHeader onMenuClick={() => setSidebarOpen(true)} />
        
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center mb-6">
              <button
                onClick={() => navigate('/projects')}
                className="flex items-center text-blue-600 hover:text-blue-700 transition-colors"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                <span className="text-sm font-medium">Back to Projects</span>
              </button>
              <i className="ri-arrow-right-s-line text-gray-400 mx-2"></i>
              <span className="text-sm text-gray-600">{categoryNames[category || '']}</span>
            </div>

            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{categoryNames[category || '']} Projects</h1>
              <p className="text-gray-600">Select a project to manage details, files, and documentation</p>
            </div>

            {/* Project Selection */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Project</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projects.map((project) => (
                    <div
                      key={project.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                        selectedProject === project.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedProject(project.id)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 mb-1">{project.name}</h3>
                          <p className="text-sm text-gray-600">{project.client}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                            {project.status}
                          </span>
                          <i className={`ri-flag-line ${getPriorityColor(project.priority)}`}></i>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500">Progress</span>
                          <span className="text-xs text-gray-900">{project.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="text-xs text-gray-500">
                        <p>Budget: {project.budget}</p>
                        <p>Due: {project.deadline}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Selected Project Info */}
            {selectedProjectData && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{selectedProjectData.name}</h2>
                      <p className="text-gray-600">{selectedProjectData.client} • {selectedProjectData.type}</p>
                      <p className="text-sm text-gray-500 mt-1">{selectedProjectData.address}</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedProjectData.status)}`}>
                        {selectedProjectData.status}
                      </span>
                      <p className="text-sm text-gray-600 mt-2">Budget: {selectedProjectData.budget}</p>
                      <p className="text-sm text-gray-600">Due: {selectedProjectData.deadline}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      disabled={!selectedProject}
                      className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center transition-colors ${
                        activeTab === tab.id && selectedProject
                          ? 'border-blue-500 text-blue-600'
                          : selectedProject
                          ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          : 'border-transparent text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      <i className={`${tab.icon} mr-2`}></i>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
